import { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Megaphone, Loader2, Sparkles, Copy } from 'lucide-react';
import { toast } from 'sonner';

const AD_PLATFORMS = ['Meta Ads', 'Google Ads', 'LinkedIn Ads'];

export const AdGeneratorPage = () => {
  const { api } = useAuth();
  const [offer, setOffer] = useState('');
  const [audience, setAudience] = useState('');
  const [tone, setTone] = useState('Punchy');
  const [brand, setBrand] = useState('');
  const [platform, setPlatform] = useState('Meta Ads');
  const [cta, setCta] = useState('Learn More');
  const [numVariants, setNumVariants] = useState(3);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const handleGenerate = async () => {
    if (!offer.trim() || !audience.trim()) {
      toast.error('Offer and Audience are required');
      return;
    }
    setLoading(true);
    setResult(null);
    try {
      const res = await api.post('/generate/ad', {
        product_or_offer: offer.trim(),
        audience: audience.trim(),
        tone,
        brand_personality: brand.trim() || undefined,
        ad_platform: platform,
        cta: cta.trim() || undefined,
        num_variants: Number(numVariants),
      });
      setResult(res.data);
      toast.success('Ad variants generated');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Generation failed');
    } finally {
      setLoading(false);
    }
  };

  const cj = result?.content_json || {};
  const variants = cj.variants || [];

  const copyVariant = (v, idx) => {
    const text = [
      v.headlines?.length ? `Headlines:\n- ${v.headlines.join('\n- ')}` : '',
      v.descriptions?.length ? `\nDescriptions:\n- ${v.descriptions.join('\n- ')}` : '',
      v.primary_text ? `\nPrimary text:\n${v.primary_text}` : '',
      v.notes ? `\nNotes: ${v.notes}` : '',
    ].filter(Boolean).join('');
    navigator.clipboard.writeText(text);
    toast.success(`Variant ${idx + 1} copied`);
  };

  return (
    <div className="space-y-6" data-testid="ad-generator-page">
      <div>
        <h1 className="text-2xl font-bold tracking-tight text-zinc-900 flex items-center gap-2">
          <Megaphone className="w-6 h-6 text-indigo-600" />
          AI Ad Copy
        </h1>
        <p className="text-zinc-500 text-sm mt-0.5">
          Generate headline / description / primary-text variants for Meta, Google, and LinkedIn Ads.
        </p>
      </div>

      <div className="grid lg:grid-cols-[380px_1fr] gap-6">
        <Card className="border-zinc-200 h-fit">
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Ad details</CardTitle>
            <CardDescription>Optimized for each platform's character limits.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="space-y-1.5">
              <Label htmlFor="ad-offer">Product or offer *</Label>
              <Input id="ad-offer" value={offer} onChange={(e) => setOffer(e.target.value)} placeholder="AI content planner for agencies" data-testid="ad-offer-input" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="ad-audience">Audience *</Label>
              <Input id="ad-audience" value={audience} onChange={(e) => setAudience(e.target.value)} placeholder="Agency owners, marketers" data-testid="ad-audience-input" />
            </div>
            <div className="space-y-1.5">
              <Label>Ad platform</Label>
              <Select value={platform} onValueChange={setPlatform}>
                <SelectTrigger data-testid="ad-platform-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {AD_PLATFORMS.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1.5">
                <Label htmlFor="ad-tone">Tone</Label>
                <Input id="ad-tone" value={tone} onChange={(e) => setTone(e.target.value)} placeholder="Punchy" data-testid="ad-tone-input" />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="ad-variants">Variants</Label>
                <Input id="ad-variants" type="number" min={1} max={5} value={numVariants} onChange={(e) => setNumVariants(e.target.value)} data-testid="ad-variants-input" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="ad-brand">Brand personality</Label>
              <Input id="ad-brand" value={brand} onChange={(e) => setBrand(e.target.value)} placeholder="friendly, bold" data-testid="ad-brand-input" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="ad-cta">CTA</Label>
              <Input id="ad-cta" value={cta} onChange={(e) => setCta(e.target.value)} placeholder="Learn More" data-testid="ad-cta-input" />
            </div>
            <Button
              className="w-full bg-indigo-600 hover:bg-indigo-700"
              onClick={handleGenerate}
              disabled={loading}
              data-testid="ad-generate-btn"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Sparkles className="w-4 h-4 mr-2" />}
              Generate variants
            </Button>
          </CardContent>
        </Card>

        <div className="space-y-4">
          {loading && (
            <Card className="border-zinc-200"><CardContent className="p-16 text-center">
              <Loader2 className="w-10 h-10 animate-spin text-indigo-600 mx-auto mb-3" />
              <p className="text-sm text-zinc-500">Writing your ad variants...</p>
            </CardContent></Card>
          )}
          {!loading && !result && (
            <Card className="border-zinc-200"><CardContent className="p-16 text-center">
              <Megaphone className="w-12 h-12 text-zinc-200 mx-auto mb-3" />
              <p className="text-sm text-zinc-500">Ad variants will appear here.</p>
            </CardContent></Card>
          )}
          {!loading && result && variants.map((v, idx) => (
            <Card key={idx} className="border-zinc-200" data-testid={`ad-variant-${idx}`}>
              <CardHeader className="pb-2 flex-row items-center justify-between space-y-0">
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className="bg-indigo-50 text-indigo-700 border-indigo-200">
                    Variant {idx + 1}
                  </Badge>
                  <Badge variant="outline">{cj.platform}</Badge>
                </div>
                <Button size="sm" variant="outline" onClick={() => copyVariant(v, idx)}>
                  <Copy className="w-3.5 h-3.5 mr-1.5" />
                  Copy
                </Button>
              </CardHeader>
              <CardContent className="space-y-3">
                {v.headlines?.length > 0 && (
                  <div>
                    <p className="text-[10px] font-semibold uppercase tracking-wide text-zinc-500 mb-1">Headlines</p>
                    <ul className="space-y-0.5 text-sm text-zinc-900">
                      {v.headlines.map((h, i) => <li key={i}>• {h}</li>)}
                    </ul>
                  </div>
                )}
                {v.descriptions?.length > 0 && (
                  <div>
                    <p className="text-[10px] font-semibold uppercase tracking-wide text-zinc-500 mb-1">Descriptions</p>
                    <ul className="space-y-0.5 text-sm text-zinc-700">
                      {v.descriptions.map((d, i) => <li key={i}>• {d}</li>)}
                    </ul>
                  </div>
                )}
                {v.primary_text && (
                  <div>
                    <p className="text-[10px] font-semibold uppercase tracking-wide text-zinc-500 mb-1">Primary text</p>
                    <p className="text-sm text-zinc-700 whitespace-pre-wrap">{v.primary_text}</p>
                  </div>
                )}
                {v.notes && (
                  <p className="text-xs text-zinc-500 italic">💡 {v.notes}</p>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
};
